//@ts-nocheck
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Component } from 'react';
import { TableHeader } from './table-header.component';
import { withRouter, RouteComponentProps } from 'react-router';
import { AppState } from '../../app.state';
import styles from './manage-equipment.styles';
import { get } from 'lodash';
import { Equipment } from '../../_shared/interfaces/equipment';
import { errorDef, checkHasFilter, findWorstError, isPB } from '../../utils';
import { isGuestUser } from '../../authentication/authentication.actions';
import { Publish } from '../../_shared/interfaces/publish';
import {
  Cards,
  getSubCatsFromEquipment,
  renderGrid,
} from './renders.component.';
import { AIcon as Icon } from '../../_shared/icon';
import { WithAppConfig } from '../../_shared/hooks/useAppConfig';
import { EQStatus } from '../../_shared/interfaces/equipmentStatus';
import { Footer } from '../../footer';

interface IProps extends RouteComponentProps {
  sState: AppState;
  setLastEquipment: (id?: string) => void;
  addToMap: (pubs: Publish[]) => Publish[];
  setEquipView: (i: number) => void;
  // AER-1612: thunk dispatcher to persist Dashboard pagination across nav.
  setEquipPage: (page: number) => void;
  // AER-1612 follow-up: thunk dispatcher to persist the user-chosen page
  // size (e.g. "20 / page") alongside `equipPage`.
  setEquipPageSize: (pageSize: number) => void;
  app?: any;
}

const PERIODIC_UPDATE = 60000;

interface IState {
  views: { icon: string }[];
  selectedView?: number;
}

const Content = {
  noEquip: {
    title: 'No Equipment Found.',
    text: `Your user account is not associated with any equipment, or there are no results from your search. If this is an error, please contact your organization administrator.`,
    textSearch: `Your user account is not associated with any equipment, or there are no results from your search. If this is an error, please contact your organization administrator.`,
  },
};

export class _ManageEquipmentComponent extends Component<IProps, IState> {
  // AER-1611: throttle re-renders via an instance field instead of state.
  // Previously this lived in state and was written from inside
  // shouldComponentUpdate via setState — a documented React anti-pattern
  // (setState during sCU schedules another update, which under React 19's
  // stricter rules can manifest as "Maximum update depth exceeded" /
  // Minified React error #185) and the symptom Danielle saw was a white
  // screen on /dashboard after the tab was left open overnight.
  lastUpdate: number | undefined = undefined;
  state: IState = {
    views: [{ icon: 'th-large' }, { icon: 'bars' }],
    // AER-1612 follow-up: the redux key written by `setEquipView` is
    // `equipView` (lowercase). The original read here used `EquipView`
    // (capital E), so the persisted grid/list choice was never picked up
    // on remount — re-entering /dashboard always fell through to the
    // length-based default. Match the casing used by the writer.
    selectedView: get(this.props.sState, 'dash.view.equipView', undefined)
  };
  sortEquipment = (equip: Equipment[]) => {
    return equip
      .sort((a, b) => {
        const aMap = get(a, 'status.alerts', []) as EQStatus["alerts"];
        const bMap = get(b, 'status.alerts', []) as EQStatus["alerts"];
        const _a = get(findWorstError(aMap, a), 'error', 0);
        const _b = get(findWorstError(bMap, b), 'error', 0);

        const aName = (a.name || '').toLowerCase();
        const bName = (b.name || '').toLowerCase();

        if (_a === _b) {
          return aName > bName ? 1 : aName < bName ? -1 : 0;
        } else if (_a > 0 || _b > 0) {
          const aDef = errorDef((_a || 0) as number).sort;
          const bDef = errorDef((_b || 0) as number).sort;

          return aDef < bDef ? 1 : -1;
        } else {
          return aName > bName ? 1 : aName < bName ? -1 : 0;
        }
      })
      .filter(e => e.equipmentSN !== 'ACR0000000');
  };
  // Persist the auto-chosen default view (card vs. list) once equipment has
  // loaded and nothing has been saved yet. Dispatching this from `render()`
  // (as the original code did) triggered the React warning
  // "Cannot update a component (Connect(Navigation)) while rendering a
  // different component (_ManageEquipmentComponent)"; doing it here, after
  // commit, is the supported place for redux side-effects.
  private persistDefaultViewIfNeeded = () => {
    if (this.state.selectedView !== undefined) return;
    if (get(this.props.sState, 'dash.view.equipView', undefined) !== undefined) return;
    const { sState, setEquipView } = this.props;
    const isGuest = isGuestUser(sState);
    const filterEquipment = get(sState, 'dash.filterEquipment');
    const e = get(sState, 'dash.equipment', []);
    const equipment = isGuest ? [] : this.sortEquipment((filterEquipment || e) as Equipment[]);
    if (equipment.length === 0) return;
    const v = equipment.length > 10 ? 1 : 0;
    setEquipView(v);
    this.setState({ selectedView: v });
  };
  componentDidMount() {
    this.persistDefaultViewIfNeeded();
  }
  componentDidUpdate() {
    this.persistDefaultViewIfNeeded();
  }
  cardClick = (equipment: Equipment, fromEdit = false) => {
    const cats = getSubCatsFromEquipment(equipment, this.props.sState);

    if (fromEdit) {
      this.props.history.push('/dashboard/editEquipment', {
        equipment,
        ...cats,
      });
    } else {
      this.props.setLastEquipment(equipment.id);
      this.props.history.push('/dashboard/equipmentDetails', {
        equipment,
        ...cats,
      });
    }
  };
  shouldComponentUpdate(nextProps: Readonly<IProps>, nextState: Readonly<IState>): boolean {
    const { sState } = nextProps;
    const loading: any = get(sState, 'dash.view.equipmentLoading');
    if (loading) { return false }

    // AER-1611: bump the throttle timestamp via an instance field — never
    // setState here. Calling setState from inside shouldComponentUpdate
    // schedules an extra render and, under React 19, can degenerate into
    // an infinite update loop after enough background redux activity
    // (e.g. Pusher equipmentCreate / userCreate firing while the tab is
    // idle), producing the "Maximum update depth exceeded" white screen.
    const goUpdate = () => {
      this.lastUpdate = Date.now();
      return true;
    }

    const timeCheck = !this.lastUpdate || Date.now() - this.lastUpdate >= PERIODIC_UPDATE;
    if (!loading && timeCheck) {
      return goUpdate();
    } else if (get(sState, 'dash.equipmentFilters') != get(this, 'props.sState.dash.equipmentFilters')) {
      return goUpdate();
    }

    if (nextState.selectedView !== this.state.selectedView) {
      return true;
    }

    if (
      get(nextProps, 'sState.dash.view.isPhoneBreak') !==
      get(this.props, 'sState.dash.view.isPhoneBreak')
    ) {
      return true;
    }

    // AER-1612: the antd List / TableList pagination is now controlled by
    // `dash.view.equipPage`. Without re-rendering on changes to this slice
    // the new `current` prop never reaches the table and the user's page
    // click does not take effect.
    if (
      get(nextProps, 'sState.dash.view.equipPage') !==
      get(this.props, 'sState.dash.view.equipPage')
    ) {
      return true;
    }

    // AER-1612 follow-up: same reason as equipPage — the grid view's
    // TableList is controlled on page size now, so a redux update must
    // re-render to propagate the new prop.
    if (
      get(nextProps, 'sState.dash.view.equipPageSize') !==
      get(this.props, 'sState.dash.view.equipPageSize')
    ) {
      return true;
    }

    return false;
  }
  render() {
    const { views } = this.state;
    let { selectedView } = this.state;
    const { sState, setEquipPage, app } = this.props;

    const loading: any = get(sState, 'dash.view.equipmentLoading');
    const filterEquipment = get(sState, 'dash.filterEquipment');
    const e = get(sState, 'dash.equipment', []);
    const isGuest = isGuestUser(sState);

    const equipment = isGuest ? [] : this.sortEquipment((filterEquipment || e) as Equipment[]);

    const hasFilter = checkHasFilter(sState);
    const ispb = isPB(sState);
    // Pure local default for first-render display only; the dispatch that
    // persists this choice to Redux lives in persistDefaultViewIfNeeded()
    // (componentDidMount/componentDidUpdate). Dispatching from render()
    // produced the "update during render" warning targeting Connect(Navigation).
    if (selectedView === undefined) {
      if (equipment.length > 10) {
        selectedView = 1;
      } else if (equipment.length > 0) {
        selectedView = 0;
      }
    }
    if (ispb) {
      selectedView = 0;
    }
    // AER-1612: read the persisted page from Redux (default 1). The renderers
    // wire `currentPage`/`setEquipPage` into the underlying antd `Table` /
    // `List` so the page survives the unmount/remount cycle when the user
    // opens an equipment item and then hits "< Back" or the Dashboard tab.
    const currentPage: number = get(sState, 'dash.view.equipPage', 1) || 1;
    // AER-1612 follow-up: persisted page size (undefined → TableList falls
    // back to AER-1607's local-state behavior with default 100). Only the
    // grid view exposes a size selector, so Cards view ignores this.
    const equipPageSize: number | undefined = get(
      sState,
      'dash.view.equipPageSize',
      undefined
    );
    const props = {
      cardClick: this.cardClick,
      equipment,
      loading,
      sState,
      pageSize: 100,
      hidePageSelect: true,
      app,
      currentPage,
      setEquipPage,
      equipPageSize,
      setEquipPageSize: this.props.setEquipPageSize,
    };

    return (
      <div css={css(styles.container)}>
        <TableHeader
          views={views}
          selectedView={selectedView!}
          onSelectView={(v: any) => {
            this.props.setEquipView(v);
            this.setState({ selectedView: v })
          }}
          equipmentCount={equipment.length ? `${equipment.length}` : '0'}
          sState={sState}
        />

        {equipment.length > 0 ? (
          selectedView === 0 ? (
            <Cards {...props} />
          ) : (
            renderGrid(props)
          )
        ) : !loading ? (
          <div css={css(styles.loadingContainer)}>
            <p css={css(styles.noEquipHeader)}>{Content.noEquip.title}</p>
            <p css={css(styles.noEquipText)}>
              {hasFilter ? Content.noEquip.textSearch : Content.noEquip.text}
            </p>
          </div>
        ) : null}

        {equipment.length === 0 && loading && (
          <div css={css(styles.loadingContainer)}>
            <Icon
              type={'loading'}
              css={css`
                font-size: 30px;
              `}
            />
          </div>
        )}

        {ispb && equipment.length === 0 && <Footer shouldExtend={true} />}
      </div>
    );
  }
}

export const ManageEquipmentComponent = WithAppConfig(
  withRouter(_ManageEquipmentComponent)
);
